/* eslint-disable no-underscore-dangle */
/**
 * Presentation helpers for the DISPATCHER board (English copy).
 *
 * Only the board's own words and groupings live here. Everything shared is
 * the operator screens' (operatorFormat.js — the day names, the ranges, the
 * route and passenger labels, the error copy, the English `copy` props for
 * the driver portal's components) and the driver portal's (portalFormat.js).
 * There is deliberately NO money formatter in this file: the board never
 * receives an amount on a trip, so it has none to print.
 *
 * Day arithmetic is `operatorFormat`'s `rangeFor`, which is the one place
 * the portal turns "This week" into two Budapest calendar days.
 */
import { formatDate, formatTime } from '../../../Utils/timezone';
import { overtimeEntries, overtimeTotalMinutes } from '../../../Utils/driverOvertime';
import { byPickup } from '../../../Utils/nowMarker';
import {
  EXPENSE_COPY_EN, longDay, plural, rangeFor, rangeOptions, shortDay, STEP_LABELS_EN,
} from '../Operator/operatorFormat';
import { looseIncludes, programmeHours } from '../portalFormat';

/**
 * The board's four one-tap windows, in the order a day is thought about:
 * what has just happened, what is happening, what is next, and the week
 * around it. Today is the live one; the rest are coverage — and which is
 * which is the API's call (`live`), never this list's.
 *
 * Yesterday is first because it is what the office is asked about — "what
 * happened with the 6 a.m. airport run" — and it was previously unreachable
 * without a calendar. "This month" left the tabs for the same reason it was
 * least useful: a month is never what a dispatcher is looking at, and the
 * calendar reaches any single day when it is.
 */
export const DISPATCHER_RANGES = rangeOptions(['yesterday', 'today', 'tomorrow', 'week']);

/**
 * The board is on a day somebody picked from the calendar, rather than on
 * any of the four windows — not a `rangeFor` key, and not a tab either: the
 * calendar is an icon beside the tabs, and what it hands back is ONE day.
 */
export const CALENDAR_KEY = 'calendar';

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The one day the calendar was given, as a window — `{ from, to }` with both
 * ends the same day, or null when there is no day to show.
 *
 * ONE DAY, NEVER A SPAN. The week tab is what a range is for; what a
 * dispatcher means by picking a date is that date. The API keeps its own cap
 * on how wide a window it will answer (BOARD_MAX_DAYS in
 * DispatcherPortalController) and nothing here can now reach it, so there is
 * no span for the UI to refuse.
 */
export const pickedDayRange = (day) => (DAY.test(day || '') ? { from: day, to: day } : null);

/** The window a tab stands for, or the day the calendar has been given. */
export const periodFor = (rangeKey, day, now = new Date()) => (
  rangeKey === CALENDAR_KEY ? pickedDayRange(day) : rangeFor(rangeKey, now)
);

/**
 * How often a LIVE board re-reads itself, in milliseconds.
 *
 * 60 seconds. The board's job is to notice a driver who has not set off, is
 * waiting at a pickup, or has gone over — none of which changes meaningfully
 * inside a minute, and all of which the office wants to see well within
 * five. It also matches the smallest unit anything on the screen is measured
 * in: a delay and an overtime clock are both shown in whole minutes, so
 * refreshing faster would not change a single character.
 *
 * Against that, each read is a PH-wide booking query — the widest one the
 * portal makes — so it is not something to run every few seconds for a
 * screen somebody leaves open all day. A minute is roughly 8 reads an hour
 * per dispatcher in normal use, which is nothing, and the clocks on screen
 * tick locally in between (`useNow`, every 30 seconds — they show whole
 * minutes), so the board never LOOKS stale between reads.
 *
 * Coverage ranges (the ones the API does not call `live`) do not poll at
 * all: nothing about next Tuesday changes minute to minute, and a
 * dispatcher who wants it fresh changes tab or comes back to the window,
 * both of which re-read. The one exception is a read that FAILED — that is
 * retried on the same beat until it lands, whatever the range.
 */
export const BOARD_REFRESH_MS = 60 * 1000;

// The driver's steps are the operator screens' own words (STEP_LABELS_EN),
// so the same tap reads the same way wherever staff see it.

/** 'Not started' — the state of a trip nobody has tapped anything on yet. */
const NOT_STARTED = 'Not started';

/** Shown beside the step of a trip the office completed before the driver did. */
export const CLOSED_BY_OFFICE = 'Closed by the office';

/**
 * What the driver last did on this trip, and when:
 * `{ label, at, tone, closedByOffice }`. `at` is the Budapest time of the
 * tap, or '' when there has been none.
 *
 * A trip the office marked Completed before "Dropped off" keeps the step
 * that was really recorded, with ITS time, and says `closedByOffice` —
 * never "Dropped off" stamped with the time of an earlier tap.
 */
export const currentStep = (trip) => {
  const state = Number(trip?.driver_status?.state);
  const at = trip?.driver_status?.date ? formatTime(trip.driver_status.date) : '';
  const step = (label, tone, when = at) => ({
    label, at: when, tone, closedByOffice: false,
  });

  if (trip?.status === 'Cancelled') return step('Cancelled', 'muted', '');
  if (state === 5) return step(STEP_LABELS_EN[5], 'bad');
  if (state === 4) return step(STEP_LABELS_EN[4], 'ok');

  const started = Number.isFinite(state) && state >= 1 && STEP_LABELS_EN[state];

  if (trip?.status === 'Completed') {
    return {
      ...step(started ? STEP_LABELS_EN[state] : NOT_STARTED, 'muted', started ? at : ''),
      closedByOffice: true,
    };
  }

  return started ? step(STEP_LABELS_EN[state], 'info') : step(NOT_STARTED, 'muted', '');
};

/** 'On the way · 11:05' — a step and when it was tapped, as the pill reads it. */
export const stepText = (step) => (step.at ? `${step.label} · ${step.at}` : step.label);

/**
 * The one thing about this trip the office should act on, if any:
 * `{ key, label, tone }`. Mirrors the API's `attention`, which is the only
 * thing that decides it — the board never works one out for itself, so the
 * rows and the summary above them can never disagree.
 *
 * `no_app` is the odd one out and deliberately so. It is not something to
 * act on: it says the driver on this trip has no portal login, so there is
 * nobody to have tapped anything and the minutes since the pickup mean
 * nothing. Almost every Pickup Hungary driver is in that state, so it is
 * drawn MUTED — a note, in the same place a "Late" pill would have been,
 * rather than a second alarm on most of the day.
 */
const ATTENTION = {
  no_show: { label: 'No-show', tone: 'bad' },
  waiting: { label: 'Waiting at pickup', tone: 'warn' },
  late: { label: 'Late', tone: 'bad' },
  no_app: { label: 'Driver not using app', tone: 'muted' },
};

/** The flags that are work. The twin of ph-core's `ACTIONABLE_ATTENTION`. */
const ACTIONABLE = ['no_show', 'waiting', 'late'];

export const attentionOf = (trip) => {
  const found = ATTENTION[trip?.attention];
  return found ? { key: trip.attention, ...found } : null;
};

/** Is this trip one the summary counts as needing attention? */
export const needsAttention = (trip) => ACTIONABLE.includes(trip?.attention);

/**
 * Is this a row the office should DO something about — the board's one
 * filter?
 *
 * Late, waiting at a pickup, a reported no-show, or a trip going ahead with
 * nobody on it. The first three are the API's `attention` (the board never
 * works one out for itself); the fourth is its `needs_driver`, and it
 * belongs here because "who has nothing on it" is the one thing a coverage
 * range is for — without it the filter would do nothing at all on any day
 * but today.
 *
 * The twin of ph-core's `rowNeedsAttention`, which counts the same rows
 * across the whole period for the chip above the list. Same two fields, same
 * `or`, on purpose: the count and the list it filters must agree.
 */
export const attentionRow = (trip) => needsAttention(trip) || Boolean(trip?.needs_driver);

/**
 * How many rows of the whole period need attention — the API's own count
 * (`summary.needs_attention`), which covers pages nobody has scrolled to yet.
 *
 * An API that does not send it yet falls back to counting the rows in hand,
 * which is right for a board that fits on one page and honest on one that
 * does not: the screen says how much of the period it has read.
 */
export const attentionCount = (summary, trips) => (
  Number.isFinite(Number(summary?.needs_attention))
    ? Number(summary.needs_attention)
    : (trips || []).filter(attentionRow).length
);

/**
 * Everything on a row a dispatcher would say out loud while somebody is on
 * the telephone: the booking reference, the customer, the driver, the
 * operator and the plate on the car.
 *
 * Nothing else — not the addresses, not the flight, not the category. A
 * search that also matched "Budapest" would match most of the board, and the
 * point of this field is to get to ONE trip.
 */
const searchableOf = (trip) => [
  trip?.booking_reference,
  trip?.customer_name,
  trip?.driver?.full_name,
  trip?.operator?.name,
  trip?.vehicle?.plate_number,
].filter(Boolean).join(' ');

/**
 * Does this row answer what was typed? An empty search matches everything.
 *
 * `looseIncludes` (portalFormat) is the portal's ONE thumb-typed matcher —
 * case-, accent- and punctuation-blind — shared with the assign sheet's
 * pickers so the board's search and a driver list can never disagree about
 * whether `kovacs` finds Kovács.
 */
export const matchesSearch = (trip, query) => looseIncludes(searchableOf(trip), query);

/**
 * The rows left after what the dispatcher asked for: what they typed, and
 * whether they tapped "needs attention".
 *
 * A FILTER, never a sort. Time order is how a dispatcher holds a day in
 * their head — 06:40, then 07:15, then the airport run at 09:00 — and a
 * screen that quietly reordered itself under a tap would cost more than the
 * ranking could ever win back. Narrowing the same list leaves every row
 * exactly where it was.
 */
export const filterTrips = (trips, { query = '', onlyAttention = false } = {}) => (
  (trips || []).filter(
    (trip) => (!onlyAttention || attentionRow(trip)) && matchesSearch(trip, query),
  )
);

/** '12 min late' — how far past its pickup time an unstarted trip is, or ''. */
export const lateBy = (trip, now = Date.now()) => {
  if (trip?.attention !== 'late' || !trip?.date_datetime) return '';

  const minutes = Math.floor((now - new Date(trip.date_datetime).getTime()) / 60000);

  return minutes > 0 ? `${minutes} min late` : '';
};

/** '18 min' — a running paid-waiting timer, ticked locally between reads. */
export const overtimeRunningFor = (trip, now = Date.now()) => {
  if (!trip?.overtime_running_since) return '';

  const minutes = Math.floor((now - new Date(trip.overtime_running_since).getTime()) / 60000);

  return `${Math.max(minutes, 0)} min`;
};

/** Does anything on this board need a clock ticking between reads? */
export const hasRunningClock = (trips) => (trips || []).some(
  (trip) => trip.overtime_running_since || trip.attention === 'late' || trip.attention === 'waiting',
);

/** The operator's name, or a readable stand-in when the row is gone. */
export const operatorName = (trip) => trip?.operator?.name || 'Unknown operator';

/* --- what a folded section says while it is closed ------------------------
 * A dispatcher must be able to decide whether a section is worth opening
 * WITHOUT opening it, so each one carries a count — the trip screen passes
 * these straight to CollapsibleSection's `summary`.
 */

/**
 * '3 stops'; '5 hours' for a programme booked as hours with no itinerary, so
 * the fold that now reads "Programme" says what is in it rather than nothing;
 * '' for a trip that is just A to B.
 */
export const stopsSummary = (trip) => {
  const stops = trip?.programme?.stops?.length || 0;
  if (stops) return plural(stops, 'stop', 'stops');

  const hours = programmeHours(trip);
  return hours ? plural(hours, 'hour', 'hours') : '';
};

/** How many named passengers this trip carries a manifest for. */
export const manifestCount = (trip) => (trip?.passenger_manifest || []).length;

/** How many paid-waiting entries have been recorded on this trip. */
export const overtimeCount = (trip) => overtimeEntries(trip?.driver_overtime).length;

/**
 * '2 entries · 35 min'. The minutes are the API's own total when it sends
 * one, so the fold agrees with the card inside it rather than counting
 * again from the entries.
 */
export const overtimeSummary = (trip) => {
  const total = trip?.overtime_total_minutes ?? overtimeTotalMinutes(trip?.driver_overtime);
  const minutes = Number(total) || 0;

  return [
    plural(overtimeCount(trip), 'entry', 'entries'),
    minutes ? `${minutes} min` : '',
  ].filter(Boolean).join(' · ');
};

/**
 * A board row identifies its SLOT — the booking, plus who is on it and in
 * what role. Two rows of one booking (a driver and a guide) differ here, and
 * it is what the assign sheet sends back to the API as `slot_operator_id`.
 */
export const slotKey = (trip) => [
  trip?._id || '',
  trip?.operator?.id || '',
  trip?.role || '',
].join('::');

/**
 * The board with one slot's row replaced by the one the assign endpoint
 * answered with — so the change shows at once, without re-reading the whole
 * board. `previous` is the row the sheet was opened on: its operator may be
 * exactly what just changed, so the row is found by where it WAS.
 */
export const replaceSlot = (trips, previous, updated) => {
  if (!updated) return trips || [];

  const target = slotKey(previous);

  return (trips || []).map((trip) => (slotKey(trip) === target ? updated : trip));
};

/** The heading a trip with no pickup time at all sits under. */
const NO_TIME_DAY = 'No time set';

/**
 * THE BOARD AS ONE LIST IN TIME ORDER, cut into Budapest days.
 *
 * It used to be grouped BY DRIVER — twenty little days, one per person, each
 * one read from the top. That answers "what is Anna doing today" and nothing
 * else, and it is not the question an office asks of a whole board: a
 * dispatcher with somebody on the telephone wants to know what is happening
 * at ten past four, across everybody. The office's own Daily Overview has
 * always read that way; the board now reads the same way, so the two screens
 * agree about the shape of a day. "What is Anna doing today" is the search
 * box — her name narrows this same list to her trips, still in time order,
 * and unlike the old grouping it reads the whole period through to answer.
 *
 * A DAY IS A SECTION, because a flat time order across a week is nonsense:
 * 08:00 Monday would sit under 19:00 Sunday with nothing to say which was
 * which. Each Budapest day is one block with its own heading, and inside it
 * the trips run in pickup order. A single-day range is simply one block.
 *
 * `offset` is where a day's first trip sits in the FLAT list, which is what
 * lets the "now" line be placed once, over the whole period, and then drawn
 * in whichever day it falls in (`nowSplitIndex` counts rows, not sections).
 *
 * @returns `{ ordered, days: [{ key, label, offset, trips }] }`
 */
export const boardByTime = (trips) => {
  const ordered = byPickup(trips);
  const days = [];

  ordered.forEach((trip, index) => {
    const key = formatDate(trip?.date_datetime);
    const last = days[days.length - 1];

    if (last && last.key === key) {
      last.trips.push(trip);
      return;
    }

    days.push({
      key,
      label: longDay(trip?.date_datetime) || NO_TIME_DAY,
      offset: index,
      trips: [trip],
    });
  });

  return { ordered, days };
};

/** 'Wed 16 Sep · 14:30', or just the time when the range is one day. */
export const whenLabel = (trip, multiDay) => [
  multiDay ? shortDay(trip?.date_datetime) : '',
  formatTime(trip?.date_datetime),
].filter(Boolean).join(' · ');

/**
 * What the board is showing, in one sentence: an activity board for the day
 * in progress, or coverage for a range that has not happened yet. The screen
 * says which rather than letting a future trip look like it has a status.
 */
export const rangeMeaning = (live) => (live
  ? 'Live — what is happening now'
  : 'Coverage — who is assigned, and what still needs a driver');

/**
 * Is there another page of this period to read?
 *
 * The API's own `has_more`. Never `total > trips.length`: `total` counts
 * BOOKINGS and `trips` counts slots (a booking with a driver and a guide is
 * two rows), so the two disagree in both directions and neither can answer
 * it. An API that sends no `has_more` is one page and done.
 */
export const hasMorePages = (board) => board?.has_more === true;

/**
 * Did the board stop short of the whole period in a way no amount of
 * scrolling can fix?
 *
 * ONLY the API's `capped`, which now means one thing: the period holds more
 * bookings than the board will serve at all. "There are more pages" is
 * `has_more`, and the answer to that is to keep reading, not to tell
 * somebody on the road to pick a shorter period.
 */
export const isBoardCapped = (board) => board?.capped === true;

/**
 * This page's rows added to the ones already held, with nothing listed twice.
 *
 * A booking taken while a dispatcher is scrolling shifts every page boundary
 * behind it, so page 3 can open with a row page 2 ended on. The slot is the
 * identity (`slotKey` — the booking, its operator and the role), the same
 * one the rows are keyed by, so a duplicate cannot reach the screen as a
 * second card of the same trip. Order is untouched: what arrives, arrives
 * after what is there, which is pickup order.
 */
export const appendPage = (held, arriving) => {
  const seen = new Set((held || []).map(slotKey));

  return [...(held || []), ...(arriving || []).filter((trip) => !seen.has(slotKey(trip)))];
};

/** '3 drivers · 7 trips' */
export const countsLabel = (summary) => [
  plural(Number(summary?.drivers_working) || 0, 'driver', 'drivers'),
  plural(Number(summary?.trips) || 0, 'trip', 'trips'),
].join(' · ');

/**
 * What the narrowed board found — the answer to "how many", said out loud
 * rather than left to be counted off the screen.
 *
 * `complete` is whether the whole period has been read. While it has not,
 * the count says "so far", because a search that has only seen the first
 * page and reports "No trips match" would be telling a dispatcher something
 * untrue about a booking that exists.
 */
export const resultsLabel = ({
  matches = 0, query = '', onlyAttention = false, complete = true,
} = {}) => {
  if (!query && !onlyAttention) return '';

  const verb = query ? ['matches', 'match'] : ['needs attention', 'need attention'];
  const found = matches
    ? `${plural(matches, 'trip', 'trips')} ${matches === 1 ? verb[0] : verb[1]}`
    : `No trips ${verb[1]}`;

  return complete ? found : `${found} so far — still reading this period`;
};

/**
 * The claims panel's words — the board sees an amount only here, as filed.
 * The type names are the operator screens' (EXPENSE_COPY_EN).
 */
export const expenseTypeLabel = (type) => EXPENSE_COPY_EN.typeName(type);

const EXPENSE_STATUS = {
  submitted: { label: 'Submitted', tone: 'warn' },
  approved: { label: 'Approved', tone: 'ok' },
  rejected: { label: 'Rejected', tone: 'bad' },
};

export const expenseStatus = (status) => EXPENSE_STATUS[status] || EXPENSE_STATUS.submitted;

/** 'Anna Kovács (driver)' / 'Beta Limo (operator)' — who filed a claim. */
export const expenseSubmitter = (expense) => {
  const by = expense?.submitted_by;
  if (!by) return '—';
  return by.name ? `${by.name} (${by.kind})` : by.kind;
};
